import { LoadingState } from "@repo/app-types/client/client";
import type {
  BalanceTransaction,
  PaymentMethodInfo,
} from "@repo/app-types/entities/balanceTransaction";
import type { Company } from "@repo/app-types/entities/company";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Label } from "@repo/ui/Label/label";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { InfoIconTooltip } from "@repo/ui/tooltip/infoIconTooltip";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconShieldCheck,
} from "@tabler/icons-react";
import { type FC, useCallback, useEffect, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import {
  fetchGiftCardBalance,
  selectBalanceInfo,
} from "../../billing/billingSlice.js";
import { AutoRefillModal } from "./autoRefillModal.js";
import { GiftCardBalanceIncreaseModal } from "./giftCardBalanceIncreaseModal.js";
import { GiftCardBalanceTable } from "./giftCardBalanceTable.js";
import { UnitAmountText } from "./unitAmountText.js";

interface GiftCardBalanceSectionProps {
  company: Company;
  defaultPaymentMethod: PaymentMethodInfo | null;
  onClickAddPaymentMethod: () => void;
  billingPortalLoadingState: LoadingState;
}

export const GiftCardBalanceSection: FC<GiftCardBalanceSectionProps> = ({
  company,
  defaultPaymentMethod,
  onClickAddPaymentMethod,
  billingPortalLoadingState,
}) => {
  const dispatch = useAppDispatch();
  const balanceInfo = useAppSelector(selectBalanceInfo);
  const { balanceUnitAmount, pendingBalanceUnitAmount } = balanceInfo;
  const balanceLoadingState = balanceInfo.status;

  const [autoRefillModalIsOpen, setAutoRefillModalIsOpen] = useState(false);
  const [balanceIncreaseModalIsOpen, setBalanceIncreaseModalIsOpen] =
    useState(false);
  const [balanceTransactionsLoadingState, setBalanceTransactionsLoadingState] =
    useState<LoadingState>(LoadingState.LOADING);
  const [balanceTransactions, setBalanceTransactions] = useState<
    BalanceTransaction[]
  >([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const companyIsInTrial =
    company.trial_expires_at && new Date(company.trial_expires_at) > new Date();

  useEffect(() => {
    dispatch(fetchGiftCardBalance());
  }, [dispatch]);

  const fetchBalanceTransactions = useCallback(async () => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.get(`/billing/balance-transactions`, {
        params: {
          page,
        },
      });

      setBalanceTransactions(data.balance_transactions);
      setPage(data.page);
      setTotalPages(data.total_pages);
      setBalanceTransactionsLoadingState(LoadingState.LOADED);
    } catch (err) {
      setBalanceTransactionsLoadingState(LoadingState.ERROR);
    }
  }, [page]);

  useEffect(() => {
    fetchBalanceTransactions();
  }, [fetchBalanceTransactions]);

  const renderCta = () => {
    // If we dont have a stripe customer ID or payment method, show a prompt to add payment method
    if (!company.billing_company_id || !defaultPaymentMethod)
      return (
        <div>
          <Button
            onClick={onClickAddPaymentMethod}
            variant={ButtonVariant.Primary}
            isLoading={billingPortalLoadingState === LoadingState.LOADING}
            isDisabled={billingPortalLoadingState === LoadingState.LOADING}
          >
            Add a payment method
          </Button>
        </div>
      );

    // Otherwise, show buttons to add to balance
    const autoRefillIsEnabled =
      company.settings.balance_refill_threshold_unit_amount &&
      company.settings.balance_refill_unit_amount;

    return (
      <>
        <div className="flex flex-row space-x-3">
          <Button
            onClick={() => {
              setBalanceIncreaseModalIsOpen(true);
            }}
            variant={ButtonVariant.Primary}
          >
            Add to Gift Card Balance
          </Button>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={() => {
              setAutoRefillModalIsOpen(true);
            }}
            icon={
              autoRefillIsEnabled ? (
                <IconCircleCheck className="h-5 w-5 text-green-500" />
              ) : (
                <IconAlertTriangle
                  className="h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              )
            }
          >
            {autoRefillIsEnabled
              ? "Auto-refill enabled"
              : "Auto-refill disabled"}
          </Button>
        </div>
      </>
    );
  };

  if (balanceTransactionsLoadingState === LoadingState.ERROR)
    return (
      <div className="mt-2 text-sm text-orange-600">
        An error occurred while loading your gift card balance. Please refresh
        and try again.
      </div>
    );

  if (balanceLoadingState === LoadingState.LOADING)
    return (
      <div className="mt-4">
        <DefaultSkeleton />
      </div>
    );

  return (
    <div>
      <div className="mt-2 mb-5 flex flex-row space-x-4">
        <div className="overflow-hidden rounded-lg bg-white px-4 py-3 shadow-sm">
          <dt className="truncate text-sm font-medium text-muted-foreground">
            {"Gift card balance"}
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
            {typeof balanceUnitAmount === "number" ? (
              <UnitAmountText unitAmount={balanceUnitAmount} />
            ) : (
              "-"
            )}
          </dd>
        </div>
        {pendingBalanceUnitAmount ? (
          <div className="overflow-hidden rounded-lg bg-white px-4 py-3 shadow-sm">
            <dt className="flex flex-row items-center space-x-1 align-middle text-sm font-medium text-muted-foreground">
              <span>{"Pending balance"}</span>
              <InfoIconTooltip>
                This balance will become available for use after credit card
                payments are successfully processed (usually within minutes).
                Please ensure that you have a valid credit card added.
              </InfoIconTooltip>
            </dt>
            <dd className="mt-1 text-3xl font-semibold tracking-tight text-foreground">
              <UnitAmountText unitAmount={pendingBalanceUnitAmount} />
            </dd>
          </div>
        ) : null}
      </div>
      <div className="mb-5 flex flex-col space-y-4">
        {renderCta()}
        <div className="text-sm text-muted-foreground">
          {`Your balance is used to send gift cards to interview participants after they complete an interview. `}
          {!companyIsInTrial && company.billing_usage_fee_percent > 0 && (
            <>
              <TooltipTrigger>
                <TooltipHoverTarget className="underline">
                  Usage fees
                </TooltipHoverTarget>
                <SimpleTooltip>{`${company.billing_usage_fee_percent}% of gift card volume allows Alpharun to provide payment processing, gift card sending, AI-powered interviews & analysis, and fraud prevention.`}</SimpleTooltip>
              </TooltipTrigger>{" "}
              are applied when you add funds to your balance.
            </>
          )}
        </div>
        <InfoBanner
          title="Balance protection built-in"
          description={`To protect your account from fraud, each completed interview is human-reviewed for authenticity
            before a gift card is sent using your balance.`}
          icon={<IconShieldCheck className="h-5 w-5 text-blue-400" />}
        />
      </div>
      <div className="my-6 flex flex-col space-y-2">
        <Label size={Size.SMALL}>Balance activity</Label>
        <GiftCardBalanceTable
          loadingState={balanceTransactionsLoadingState}
          balanceTransactions={balanceTransactions}
          onClickNextPage={() => setPage(page + 1)}
          onClickPreviousPage={() => setPage(page - 1)}
          page={page}
          totalPages={totalPages}
        />
      </div>
      <GiftCardBalanceIncreaseModal
        isOpen={balanceIncreaseModalIsOpen}
        onClose={() => {
          setBalanceIncreaseModalIsOpen(false);
        }}
        onAddToBalance={() => {
          fetchGiftCardBalance();
          fetchBalanceTransactions();
        }}
      />
      <AutoRefillModal
        isOpen={autoRefillModalIsOpen}
        onClose={() => {
          setAutoRefillModalIsOpen(false);
        }}
        company={company}
      />
    </div>
  );
};
